/* eslint-disable @typescript-eslint/naming-convention */
import { ActionReducerMapBuilder, PayloadAction, createSlice } from '@reduxjs/toolkit'
import { getParamsFromUrl } from '@astronautsid/wpe-utils'
import { withLoadingReducer } from 'utils/reducerHandler'
import {
  GetInventoryLocationParamsType,
  GetInventoryLocationResponseType,
} from 'features/Product/services/inventoryDetail'
import { GetInventoriesFeatureFlagResponseType } from 'features/Product/services/stockDetail'
import {
  fetchGetEligibleAssets,
  fetchGetEligibleRack,
  fetchGetInventoriesFeatureFlag,
  fetchGetInventoryLocation,
  fetchGetInventoryLocationList,
  fetchGetProductInventoryStatus,
  fetchGetProductInventoryStatusNotes,
  fetchPostInventoryTransferBySku,
  fetchPostInventoryTransferByAsset,
  fetchPostInventoryLocation,
  SLICE_NAME,
} from './thunk'

type InventoryDetailParamsType = {
  product_id: number
  product_name: string
  sku: number
  location_id: number
  location_name: string
  location_type: string
}

export type ConstantType = {
  id: number
  name: string
}

export type InventoryDetailInitialStateType = {
  isLoading: boolean
  featuresFlag: GetInventoriesFeatureFlagResponseType
  query: InventoryDetailParamsType
  filter: GetInventoryLocationParamsType
  data: GetInventoryLocationResponseType['data']
  pagination: GetInventoryLocationResponseType['pagination']
  constants: {
    status: ConstantType[]
    statusNotes: (ConstantType & {
      status_id: number
    })[]
    eligibleRacks: ConstantType[]
    eligibleAssets: ConstantType[]
  }
  popup: {
    popupAddProduct: {
      isOpen: boolean
    }
    popupPrintQr: {
      isOpen: boolean
      item: Nullable<{
        productId: number
        locationId: number
        stock: number
        expiryDate?: number
        vendorId?: number
        productAge?: number
        manufacturingDate?: number
      }>
    }
    popupChangeStatus: {
      isOpen: boolean
      item: Nullable<GetInventoryLocationResponseType['data'][0]>
      props: {
        fromStatus: Nullable<{
          id: number
          name: string
        }>
        toStatus: Nullable<{
          id: number
          name: string
        }>
      }
    }
    popupChangeExpiryDate: {
      isOpen: boolean
      item: Nullable<GetInventoryLocationResponseType['data'][0]>
    }
    popupMoveSku: {
      isOpen: boolean
      item: Nullable<GetInventoryLocationResponseType['data'][0]>
    }
    popupMovePallet: {
      isOpen: boolean
      item: Nullable<GetInventoryLocationResponseType['data'][0]>
      inventories: GetInventoryLocationResponseType['data']
    }
  }
}

const initialState: InventoryDetailInitialStateType = {
  isLoading: false,
  featuresFlag: {
    is_qr: false,
    is_tanggung_renteng: false,
    is_multi_sku: false,
    manage_ed: false,
  },
  query: getParamsFromUrl(window.location.href) as InventoryDetailParamsType,
  filter: {
    'pagination.page_index': 1,
    location_id: getParamsFromUrl(window.location.href).location_id || 0,
    product_ids: getParamsFromUrl(window.location.href).product_id || 0,
    status: '',
  },
  data: [],
  pagination: {
    page_size: 20,
    page_index: 1,
    direction: 'asc',
    sort: '',
  },
  constants: {
    status: [],
    statusNotes: [],
    eligibleRacks: [],
    eligibleAssets: [],
  },
  popup: {
    popupAddProduct: {
      isOpen: false,
    },
    popupPrintQr: {
      isOpen: false,
      item: null,
    },
    popupChangeStatus: {
      isOpen: false,
      item: null,
      props: {
        fromStatus: null,
        toStatus: null,
      },
    },
    popupChangeExpiryDate: {
      isOpen: false,
      item: null,
    },
    popupMoveSku: {
      isOpen: false,
      item: null,
    },
    popupMovePallet: {
      isOpen: false,
      item: null,
      inventories: [],
    },
  },
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetState: () => initialState,
    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload
    },
    setQuery: (state, { payload }) => {
      state.query = {
        ...state.query,
        ...payload,
      }
    },
    setFilter: (state, { payload }) => {
      state.filter = {
        ...state.filter,
        ...payload,
      }
    },
    setOpenPopupAddProduct: (state, { payload }: PayloadAction<boolean>) => {
      state.popup.popupAddProduct.isOpen = payload
    },
    setOpenPopupPrintQr: (
      state,
      { payload }: PayloadAction<InventoryDetailInitialStateType['popup']['popupPrintQr']>,
    ) => {
      state.popup.popupPrintQr = payload
    },
    setOpenPopupChangeStatus: (
      state,
      { payload }: PayloadAction<InventoryDetailInitialStateType['popup']['popupChangeStatus']>,
    ) => {
      state.popup.popupChangeStatus = payload
    },
    setOpenPopupChangeExpiryDate: (
      state,
      { payload }: PayloadAction<InventoryDetailInitialStateType['popup']['popupChangeExpiryDate']>,
    ) => {
      state.popup.popupChangeExpiryDate = payload
    },
    setOpenPopupMoveSku: (
      state,
      { payload }: PayloadAction<InventoryDetailInitialStateType['popup']['popupMoveSku']>,
    ) => {
      state.popup.popupMoveSku = payload
    },
    setOpenPopupMovePallet: (
      state,
      { payload }: PayloadAction<InventoryDetailInitialStateType['popup']['popupMovePallet']>,
    ) => {
      state.popup.popupMovePallet = payload
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<InventoryDetailInitialStateType>) => {
      builder
        .addCase(fetchGetInventoryLocation.pending, (state, action) => {
          const { arg } = action.meta

          state.filter = {
            ...state.filter,
            ...arg,
          }
        })
        .addCase(fetchGetInventoryLocation.fulfilled, (state, action) => {
          const {
            data,
            pagination: { direction, page_index, page_size, sort },
          } = action.payload

          state.data = data
          state.pagination = {
            page_size,
            direction,
            page_index,
            sort,
          }
        })
        .addCase(fetchGetInventoryLocationList.fulfilled, (state, action) => {
          const { data } = action.payload

          state.popup.popupMovePallet.inventories = data
        })
        .addCase(fetchGetProductInventoryStatus.fulfilled, (state, action) => {
          state.constants.status = action.payload.map((item) => ({
            id: item.id,
            name: item.displayName,
          }))
        })
        .addCase(fetchGetProductInventoryStatusNotes.fulfilled, (state, action) => {
          state.constants.statusNotes = action.payload
        })
        .addCase(fetchGetInventoriesFeatureFlag.fulfilled, (state, action) => {
          state.featuresFlag = action.payload as GetInventoriesFeatureFlagResponseType
        })
        .addCase(fetchGetEligibleRack.fulfilled, (state, action) => {
          state.constants.eligibleRacks = action.payload
        })
        .addCase(fetchGetEligibleAssets.fulfilled, (state, action) => {
          state.constants.eligibleAssets = action.payload.map((item) => ({
            id: item.id,
            name: item.code,
          }))
        })
    },
    [
      fetchGetInventoryLocation,
      fetchGetInventoryLocationList,
      fetchPostInventoryLocation,
      fetchPostInventoryTransferBySku,
      fetchPostInventoryTransferByAsset,
    ],
  ),
})

export const {
  resetState,
  setIsLoading,
  setQuery,
  setFilter,
  setOpenPopupAddProduct,
  setOpenPopupPrintQr,
  setOpenPopupChangeStatus,
  setOpenPopupChangeExpiryDate,
  setOpenPopupMoveSku,
  setOpenPopupMovePallet,
} = slice.actions
export default slice.reducer
